import { AppContextImpl, AppContext } from "./application-context";
import { AgentProfileEntity } from "../entity-service-implementation/agent-profile-entity-model";
import { UserProfile } from "../userProfileModel";
import { HeuristicallyGeneratedLocalStorageKeyMap } from "../heuristically-generated-local-storage-key-map";
import { MsalAuthenticationServiceImplementation } from "../msal/msal-authentication-service";

export const appContextPersistenceKey = "sdApplicationContext";

export interface ApplicationContextService {
  appContext: AppContext;
  incrementCV(): string;

  /** Attaches agent details, when on servicedeskdev tenant, using userProfile service
   *  TODO: Once we shift to using agentProfileEntityService for all tenants, remove this method.
   */
  attachProfileDetails(details: any): void;

  /** Attaches agent profile details, when on keystone tenant, using agentProfileEntityService service */
  attachAgentProfileDetails(details: any): void;
  isSigningIn(): boolean;
  shutdownApp(): void;
}

export class ApplicationContextServiceImpl implements ApplicationContextService {
  private localStorage: Storage;

  // @ngInject
  constructor(
    $rootScope: ng.IRootScopeService,
    private applicationContext: AppContextImpl,
    private msalAuthenticationService: MsalAuthenticationServiceImplementation,
    private $window: ng.IWindowService
  ) {
    this.localStorage = this.$window.localStorage;
  }

  public get appContext(): AppContext {
    return this.applicationContext;
  }

  public isSigningIn(): boolean {
    return this.msalAuthenticationService.loginInProgress() && !!this.msalAuthenticationService.userInfo;
  }

  /** Attaches agent details, when on servicedeskdev tenant, using userProfile service
   *  TODO: Once we shift to using agentProfileEntityService for all tenants, remove this method.
   */
  public attachProfileDetails(userProfile: UserProfile) {
    // As this is the legacy profile lookup system, do not overwrite already reported values
    if (userProfile.AgentId && !this.applicationContext.agentPuid) {
      this.applicationContext.agentPuid = userProfile.AgentId;
    }
    if (userProfile.VendorSiteCode && !this.applicationContext.vendorSiteId) {
      this.applicationContext.vendorSiteId = userProfile.VendorSiteCode;
    }
    if (userProfile.Site && !this.applicationContext.vendorSiteName) {
      this.applicationContext.vendorSiteName = userProfile.Site;
    }
    if (userProfile.AgentEmail && !this.applicationContext.agentEmail) {
      this.applicationContext.agentEmail = userProfile.AgentEmail;
    }

    if (userProfile.Mode && !this.applicationContext.agentMode) {
      this.applicationContext.agentMode = userProfile.Mode;
    }
    if (userProfile.DisplayName && !this.applicationContext.agentDisplayName) {
      this.applicationContext.agentDisplayName = userProfile.DisplayName;
    }
    if (userProfile.ImageUrl && !this.applicationContext.agentProfileImageUrl) {
      this.applicationContext.agentProfileImageUrl = userProfile.ImageUrl;
    }
    if (userProfile.AgentUPN) {
      this.applicationContext.agentId = userProfile.AgentId;
    }
    this.onUpdateAppContext();
  }

  /** Attaches agent profile details, when on keystone tenant, using agentProfileEntityService service */
  public attachAgentProfileDetails(agentProfile: AgentProfileEntity) {
    if (agentProfile.id) {
      this.applicationContext.agentPuid = agentProfile.msaPuid;
    }
    if (agentProfile.siteId) {
      this.applicationContext.vendorSiteId = agentProfile.siteId;
    }
    if (agentProfile.siteName) {
      this.applicationContext.vendorSiteName = agentProfile.siteName;
    }
    if (agentProfile.email) {
      this.applicationContext.agentEmail = agentProfile.email;
    }
    if (agentProfile.extensionProperties) {
      this.applicationContext.agentMode = agentProfile.extensionProperties["Mode"];
    }
    if (agentProfile.name) {
      this.applicationContext.agentDisplayName = agentProfile.name;
    }
    if (agentProfile.imageUrl) {
      this.applicationContext.agentProfileImageUrl = agentProfile.imageUrl;
    }
    if (agentProfile.aadUpn) {
      this.applicationContext.agentId = agentProfile.id;
      this.applicationContext.agentUpn = agentProfile.aadUpn;
      if (!this.applicationContext.agentEmail) {
        // Utilize the UPN if profile lacks an Email address
        this.applicationContext.agentEmail = `UPN:${agentProfile.aadUpn}`;
      }
    }
    this.onUpdateAppContext();
  }

  public incrementCV() {
    this.applicationContext.windowCV = (<any>this.$window).awa.cv.increment();
    this.onUpdateAppContext();
    return this.appContext.windowCV;
  }

  public shutdownApp() {
    this.localStorage.removeItem(appContextPersistenceKey);
    HeuristicallyGeneratedLocalStorageKeyMap.runMemoryLeakingGlobalCleanup();
  }

  private onUpdateAppContext() {
    this.localStorage.setItem(appContextPersistenceKey, JSON.stringify(this.appContext));
  }
}
